import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Menu, Transition } from "@headlessui/react";
import {
  Bars3BottomLeftIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import Avatar from "../../user/Avatar";
import classNames from "../../../utils/classNames";
import { useAuthApi } from "../../../hooks/useAuthApi";
import CurrentBasket from "../../basket/CurrentBasket";
import { UsageTopup } from "../../usage/UsageTopup";
import useCustomerDetails from "../../../data/useCustomerDetails";
import useApiHelper from "../../../hooks/useApiHelper";
import { DefaultResponseWithData } from "../../../models/system";

export interface TopbarProps {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

type SearchType = "Lead" | "Vehicle" | "Part" | "Listing";

interface Vehicle {
  id: string;
}

interface Lead {
  id: string;
}

interface PartData {
  id: string;
}

export default function Topbar({ collapsed, onToggleCollapsed, setSidebarOpen }: TopbarProps) {
  const { logout } = useAuthApi();
  const { brand } = useCustomerDetails();
  const brandLogo = brand.data?.data.logo;
  const brandCroppedLogo = brand.data?.data.croppedLogo;
  const navigate = useNavigate();
  const { get } = useApiHelper();

  const [searchType, setSearchType] = useState<SearchType>("Lead");
  const [searchValue, setSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const handleLogout = async () => {
    // TODO: impl logout with sso
    // For now manual cookie deletion
    // // Delete the cookie by setting an expiration date in the past
    // document.cookie = "WilmaAuthToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    // // Optionally redirect to a login page or home page after logout
    // window.location.href = "/"; // Update with the correct path for your app
    logout().then(() => {
      window.location.href = "/";
    });
  };

  const passwordReset = () => {
    //TODO: impl password reset
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchValue.trim()) {
      return;
    }

    setIsSearching(true);

    try {
      if (searchType === "Part" || searchType === "Listing") {
        // Search by tag number
        const response = await get<DefaultResponseWithData<PartData>>(
          `/part/bytag/${encodeURIComponent(searchValue.trim())}`
        );

        if (response.success && response.data) {
          if (searchType === "Part") {
            navigate(`/stock/${response.data.id}`);
          } else {
            // Listing search
            navigate(`/ebay-listings/${response.data.id}`);
          }
        } else {
          // No exact match found, redirect to table with search parameter
          if (searchType === "Part") {
            navigate(`/stock?search=${encodeURIComponent(searchValue.trim())}`);
          } else {
            navigate(`/ebay-listings?search=${encodeURIComponent(searchValue.trim())}`);
          }
        }
      } else {
        // Search by VRM for Lead or Vehicle
        const response = await get<DefaultResponseWithData<Vehicle>>(
          `/vehicle/lookup/${encodeURIComponent(searchValue.trim())}`
        );

        if (response.success && response.data) {
          const vehicleId = response.data.id;

          if (searchType === "Lead") {
            // Get the lead for this vehicle
            try {
              const leadResponse = await get<DefaultResponseWithData<Lead>>(
                `/vehicle/${vehicleId}/lead`
              );

              if (leadResponse.success && leadResponse.data) {
                navigate(`/leads/${leadResponse.data.id}`);
              } else {
                // No lead found, redirect to leads table with search parameter
                navigate(`/leads?search=${encodeURIComponent(searchValue.trim())}`);
              }
            } catch (error) {
              // No lead found, redirect to leads table with search parameter
              navigate(`/leads?search=${encodeURIComponent(searchValue.trim())}`);
            }
          } else {
            // Vehicle search
            navigate(`/vehicles/${vehicleId}`);
          }
        } else {
          // No exact match found, redirect to table with search parameter
          if (searchType === "Lead") {
            navigate(`/leads?search=${encodeURIComponent(searchValue.trim())}`);
          } else {
            navigate(`/vehicles?search=${encodeURIComponent(searchValue.trim())}`);
          }
        }
      }
    } catch (error: any) {
      // On error, redirect to appropriate table with search parameter
      const searchParam = encodeURIComponent(searchValue.trim());
      if (searchType === "Lead") {
        navigate(`/leads?search=${searchParam}`);
      } else if (searchType === "Vehicle") {
        navigate(`/vehicles?search=${searchParam}`);
      } else if (searchType === "Part") {
        navigate(`/stock?search=${searchParam}`);
      } else {
        navigate(`/ebay-listings?search=${searchParam}`);
      }
    } finally {
      setIsSearching(false);
      setSearchValue("");
    }
  };

  const userNavigation = [
    { name: "Your Profile", href: "#" },
    { name: "Reset password", onClick: passwordReset },
    { name: "Sign out", onClick: handleLogout },
  ];

  return (
    <div className="sticky top-0 z-30 flex h-16 flex-shrink-0 bg-white shadow">
      {/* For mobile sidebar */}
      <button
        type="button"
        className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
      </button>


      <div className="flex flex-1 justify-between">

        <div className="flex flex-shrink-0 items-center justify-center">
          {/* For desktop collapsing */}
          <button
            type="button"
            className="border-r border-gray-200 hover:bg-gray-100 px-4 h-full w-16 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:block hidden"
            onClick={onToggleCollapsed}
          >
            <span className="sr-only">Collapse Sidebar</span>
            <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className={`px-4`}>
            <img className="h-10 hidden md:block w-auto object-contain" src={brandLogo} alt="Company Logo" />
            <img className="h-8 md:hidden w-auto object-contain" src={brandCroppedLogo} alt="Company Logo" />
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex flex-1 items-center justify-end px-4">
          <form className="flex w-full max-w-lg" onSubmit={handleSearch}>
            <div className="relative flex flex-1 items-center">
              {/* Search Type Dropdown */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex h-10 items-center gap-x-1 rounded-l-md border border-r-0 border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500">
                  {searchType}
                  <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 mt-2 w-32 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      {(["Lead", "Vehicle", "Part", "Listing"] as SearchType[]).map((type) => (
                        <Menu.Item key={type}>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => setSearchType(type)}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "w-full px-4 py-2 text-left text-sm text-gray-700"
                              )}
                            >
                              {type}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* Search Input */}
              <div className="relative flex-1">
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
                <div className="relative w-full text-gray-400 focus-within:text-gray-600">
                  <input
                    id="search-field"
                    className="block h-10 w-full border border-l-0 border-r-0 border-gray-300 py-2 pr-3 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                    placeholder={searchType === "Part" || searchType === "Listing" ? "Tag number" : "VRM"}
                    type="search"
                    name="search"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    disabled={isSearching}
                  />
                </div>
              </div>

              {/* Search Button */}
              <button
                type="submit"
                disabled={isSearching || !searchValue.trim()}
                className={classNames(
                  "flex h-10 items-center justify-center rounded-r-md border border-l-0 border-transparent px-3 py-2 text-sm font-medium leading-4 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500",
                  "bg-primary-600 hover:bg-primary-700 disabled:bg-gray-500 disabled:hover:bg-gray-500 text-white disabled:cursor-not-allowed"
                )}
              >
                <span className="sr-only">Search</span>
                <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </form>
        </div>
        <div className="mx-4 flex items-center gap-x-4 md:ml-6">
          {/* <button
            type="button"
            className="rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <span className="sr-only">View notifications</span>
            <BellIcon className="h-6 w-6" aria-hidden="true" />
          </button> */}

          <UsageTopup />

          <CurrentBasket />

          {/* Profile dropdown */}
          <Menu as="div" className="relative">
            <div>
              <Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-transform hover:scale-110">
                <span className="sr-only">Open user menu</span>
                <Avatar classes="h-9 w-9" />
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {userNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      <a
                        // href={item.href}
                        onClick={item.onClick}
                        className={classNames(
                          active ? "bg-gray-100" : "",
                          "block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                        )}
                      >
                        {item.name}
                      </a>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
}
